import { signal, whenReadable, writable } from 'easy-signal';
import { EMPTY_OBJECT } from '../util';
import { getClientDoc } from './clientDoc';
export function proxyDoc(call, path, docConfig, docSyncs, clientDocs = new Map()) {
    let unsubscribes = [];
    let doc;
    const isLoaded = writable(false);
    const hasLocalChanges = writable(false);
    const onChange = signal();
    const onTextChange = signal();
    const whenPathSet = whenReadable(path);
    let shouldLoad = false;
    // This will be what we interact with in the app. It will forward all calls to the actual doc when it is set.
    const { get, set, subscribe } = writable(EMPTY_OBJECT);
    path.subscribe(onPathUpdate); // This subscribes before the promise is created
    function onPathUpdate(path) {
        // Reset the facade and unsubscribe from the old doc
        if (unsubscribes.length) {
            unsubscribes.forEach(u => u());
            unsubscribes.length = 0;
            isLoaded.set(false);
            hasLocalChanges.set(false);
        }
        if (!path) {
            doc = null;
            set(EMPTY_OBJECT);
        }
        else {
            let clientDoc = clientDocs.get(path)?.deref();
            if (!clientDoc) {
                clientDoc = getClientDoc(call, path, docConfig, docSyncs);
                clientDocs.set(path, new WeakRef(clientDoc));
            }
            doc = clientDoc;
            unsubscribes = [
                doc.subscribe(v => set(v)),
                doc.isLoaded.subscribe(v => isLoaded.set(v)),
                doc.hasLocalChanges.subscribe(v => hasLocalChanges.set(v)),
                doc.onChange(onChange),
                doc.onTextChange(onTextChange),
            ];
            if (shouldLoad)
                doc.load();
        }
    }
    return {
        get,
        subscribe,
        isLoaded,
        hasLocalChanges,
        onChange,
        onTextChange,
        async sync() {
            await whenPathSet;
            return doc.sync();
        },
        async unsync() {
            await whenPathSet;
            return doc.unsync();
        },
        async load(forceReload) {
            shouldLoad = true;
            await whenPathSet;
            return doc.load(forceReload);
        },
        unload() {
            shouldLoad = false;
            if (doc)
                doc.unload();
        },
        async patch(patch, pathPrefix = '') {
            await whenPathSet;
            if (!doc.patch)
                throw new Error('This doc is read-only');
            return doc.patch(patch, pathPrefix);
        },
        async update(updater, pathPrefix = '') {
            await whenPathSet;
            if (!doc.update)
                throw new Error('This doc is read-only');
            return doc.update(updater, pathPrefix);
        },
        async delete() {
            await whenPathSet;
            if (!doc.delete)
                throw new Error('This doc is read-only');
            return doc.delete();
        },
    };
}
